import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Plus, Edit3, Trash2, Home, Building2, MapPin, Users, Layers, DollarSign } from 'lucide-react';
import PropertyForm from '../components/properties/PropertyForm';
import { PageHeader, Card, StatTile, Badge, EmptyState } from '../components/ui';

const statusTone = s => ({
    available: 'neutral',
    occupied: 'success',
    maintenance: 'warning',
    unavailable: 'neutral',
}[s] || 'neutral');

const Properties = () => {
    const [properties, setProperties] = useState([]);
    const [summary, setSummary] = useState({});
    const [buildings, setBuildings] = useState([]);
    const [groupBy, setGroupBy] = useState('none');
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [params] = useSearchParams();
    const presetBuildingId = params.get('building_id');

    const load = async () => {
        setLoading(true);
        try {
            const [p, s, b] = await Promise.all([
                axios.get('/api/properties'),
                axios.get('/api/properties/summary').catch(() => ({ data: {} })),
                axios.get('/api/buildings').catch(() => ({ data: { buildings: [] } })),
            ]);
            setProperties(p.data.properties || []);
            setSummary(s.data || {});
            setBuildings(b.data.buildings || []);
        } catch { toast.error('Failed to load properties'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    useEffect(() => { if (presetBuildingId) setShowForm(true); }, [presetBuildingId]);

    const handleCreate = async (data) => {
        try { await axios.post('/api/properties', data); toast.success('Property added'); load(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); throw err; }
    };
    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        try { await axios.delete(`/api/properties/${id}`); toast.success('Deleted'); load(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const grouped = React.useMemo(() => {
        if (groupBy !== 'building') return null;
        const map = new Map();
        for (const p of properties) {
            const key = p.building_display_name || p.building_name?.trim() || '— Standalone units —';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(p);
        }
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [properties, groupBy]);

    return (
        <div className="animate-fade-in">
            <PageHeader
                title="Properties"
                subtitle="Every rental unit in your portfolio, standalone or part of a building."
                actions={
                    <>
                        <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
                                className="input py-2 text-sm w-auto">
                            <option value="none">Flat list</option>
                            <option value="building">Group by building</option>
                        </select>
                        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary">
                            <Plus className="h-4 w-4" /> New property
                        </button>
                    </>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <StatTile label="Total units" value={summary.total_properties || 0} icon={Building2} accent="primary" />
                <StatTile label="Occupied"    value={summary.occupied || 0}         icon={Users}     accent="success" />
                <StatTile label="Available"   value={summary.available || 0}        icon={Home}      accent="primary" />
                <StatTile label="Monthly potential" value={formatCurrency(summary.potential_monthly_income || 0)} icon={DollarSign} accent="primary" />
            </div>

            {buildings.length > 0 && groupBy === 'none' && (
                <Card title="Buildings" subtitle={`${buildings.length} building${buildings.length !== 1 ? 's' : ''}`}
                      action={<Link to="/buildings" className="text-xs text-primary-700 hover:text-primary-800 font-medium">Manage →</Link>}
                      className="mb-6">
                    <div className="flex flex-wrap gap-2">
                        {buildings.map(b => (
                            <Link key={b.id} to={`/buildings/${b.id}`}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-neutral-50 hover:bg-primary-50 rounded-lg text-xs transition-colors">
                                <Building2 className="h-3.5 w-3.5 text-primary-700" />
                                <span className="font-medium text-neutral-900">{b.name}</span>
                                <span className="text-neutral-500">·</span>
                                <span className="text-neutral-500">{b.occupied_units || 0}/{b.units_count || 0} occupied</span>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1,2,3].map(i => <div key={i} className="h-56 bg-white rounded-xl border border-neutral-200/70 animate-pulse-soft" />)}
                </div>
            ) : properties.length === 0 ? (
                <EmptyState
                    icon={Building2}
                    title="No properties yet"
                    description="Add your first property. If it belongs to a building with multiple units, you can create the building and add units to it."
                    action={<button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary">
                        <Plus className="h-4 w-4" /> Add property
                    </button>}
                />
            ) : groupBy === 'building' ? (
                <div className="space-y-6">
                    {grouped.map(([building, units]) => (
                        <section key={building}>
                            <div className="flex items-center gap-2 mb-3">
                                <Building2 className="h-4 w-4 text-primary-700" />
                                <h2 className="text-sm font-semibold text-neutral-900">{building}</h2>
                                <span className="text-xs text-neutral-500">({units.length} unit{units.length !== 1 ? 's' : ''})</span>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {units.map(p => (
                                    <UnitCard key={p.id} p={p}
                                              onEdit={() => { setEditing(p); setShowForm(true); }}
                                              onDelete={() => handleDelete(p.id, p.name)} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {properties.map(p => (
                        <UnitCard key={p.id} p={p}
                                  onEdit={() => { setEditing(p); setShowForm(true); }}
                                  onDelete={() => handleDelete(p.id, p.name)} />
                    ))}
                </div>
            )}

            <PropertyForm
                isOpen={showForm && !editing}
                onClose={() => setShowForm(false)}
                onSubmit={handleCreate}
                presetBuildingId={presetBuildingId}
            />
            {showForm && editing && (
                <EditPropertyModal
                    property={editing}
                    buildings={buildings}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSaved={() => { setShowForm(false); setEditing(null); load(); }} />
            )}
        </div>
    );
};

const UnitCard = ({ p, onEdit, onDelete }) => (
    <div className="card card-hover overflow-hidden">
        <div className="p-5">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="font-semibold text-neutral-900 truncate">
                        {p.house_number && <span className="text-primary-700 mr-1">#{p.house_number}</span>}
                        {p.name}
                    </h3>
                    {(p.building_display_name || p.building_name) && (
                        <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                            <Building2 className="h-3 w-3" /> {p.building_display_name || p.building_name}
                        </p>
                    )}
                    <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" /> {p.address}, {p.city}
                    </p>
                </div>
                <Badge tone={statusTone(p.status)} dot>{p.status}</Badge>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div><div className="text-xs text-neutral-500">Type</div><div className="text-sm capitalize text-neutral-900">{p.property_type}</div></div>
                <div><div className="text-xs text-neutral-500">Beds</div><div className="text-sm tabular-nums text-neutral-900">{p.bedrooms ?? '—'}</div></div>
                <div><div className="text-xs text-neutral-500">Baths</div><div className="text-sm tabular-nums text-neutral-900">{p.bathrooms ?? '—'}</div></div>
            </div>

            <div className="mt-4 pt-4 border-t border-neutral-100 flex justify-between items-center">
                <div>
                    <div className="text-xs text-neutral-500">Rent / month</div>
                    <div className="text-lg font-semibold text-primary-700 tabular-nums">{formatCurrency(p.rent_amount)}</div>
                </div>
                <div className="text-right">
                    <div className="text-xs text-neutral-500">{p.active_tenants} tenant{p.active_tenants !== 1 ? 's' : ''}</div>
                    {Number(p.outstanding_balance) > 0 && (
                        <div className="text-xs text-danger-700 font-medium">Owed: {formatCurrency(p.outstanding_balance)}</div>
                    )}
                </div>
            </div>
        </div>
        <div className="flex border-t border-neutral-100">
            <button onClick={onEdit}
                    className="flex-1 py-2 text-xs text-neutral-600 hover:bg-neutral-50 inline-flex items-center justify-center gap-1">
                <Edit3 className="h-3 w-3" /> Edit
            </button>
            <button onClick={onDelete}
                    className="flex-1 py-2 text-xs text-danger-600 hover:bg-danger-50 border-l border-neutral-100 inline-flex items-center justify-center gap-1">
                <Trash2 className="h-3 w-3" /> Delete
            </button>
        </div>
    </div>
);

const EditPropertyModal = ({ property, buildings, onClose, onSaved }) => {
    const [f, setF] = useState({
        name: property.name || '',
        building_id: property.building_id || '',
        house_number: property.house_number || '',
        address: property.address || '',
        city: property.city || '',
        zip_code: property.zip_code || '',
        property_type: property.property_type || 'apartment',
        bedrooms: property.bedrooms || '',
        bathrooms: property.bathrooms || '',
        square_feet: property.square_feet || '',
        rent_amount: property.rent_amount || '',
        deposit_amount: property.deposit_amount || '',
        description: property.description || '',
        status: property.status || 'available',
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));

    const submit = async e => {
        e.preventDefault(); setSaving(true);
        try { await axios.put(`/api/properties/${property.id}`, f); toast.success('Saved'); onSaved(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-lg animate-slide-up">
                <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
                    <h2 className="text-sm font-semibold">Edit {property.name}</h2>
                    <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">✕</button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <Field label="Property name"><input required className="input" value={f.name} onChange={e => set('name', e.target.value)} /></Field>
                    <Field label="Type"><select className="input" value={f.property_type} onChange={e => set('property_type', e.target.value)}>
                        {['apartment','house','condo','commercial','land'].map(t => <option key={t}>{t}</option>)}
                    </select></Field>
                    <Field label="Building" span2>
                        <select className="input" value={f.building_id} onChange={e => set('building_id', e.target.value)}>
                            <option value="">— Standalone —</option>
                            {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </Field>
                    <Field label="House / unit #"><input className="input" value={f.house_number} onChange={e => set('house_number', e.target.value)} /></Field>
                    <Field label="Status"><select className="input" value={f.status} onChange={e => set('status', e.target.value)}>
                        {['available','occupied','maintenance','unavailable'].map(s => <option key={s}>{s}</option>)}
                    </select></Field>
                    <Field label="Address" span2><input required className="input" value={f.address} onChange={e => set('address', e.target.value)} /></Field>
                    <Field label="City"><input required className="input" value={f.city} onChange={e => set('city', e.target.value)} /></Field>
                    <Field label="ZIP"><input required className="input" value={f.zip_code} onChange={e => set('zip_code', e.target.value)} /></Field>
                    <Field label="Bedrooms"><input type="number" className="input" value={f.bedrooms} onChange={e => set('bedrooms', e.target.value)} /></Field>
                    <Field label="Bathrooms"><input type="number" step="0.5" className="input" value={f.bathrooms} onChange={e => set('bathrooms', e.target.value)} /></Field>
                    <Field label="Sq ft"><input type="number" className="input" value={f.square_feet} onChange={e => set('square_feet', e.target.value)} /></Field>
                    <Field label="Monthly rent (KES)"><input required type="number" step="0.01" className="input" value={f.rent_amount} onChange={e => set('rent_amount', e.target.value)} /></Field>
                    <Field label="Deposit (KES)"><input type="number" step="0.01" className="input" value={f.deposit_amount} onChange={e => set('deposit_amount', e.target.value)} /></Field>
                    <Field label="Description" span2><textarea rows={2} className="input" value={f.description} onChange={e => set('description', e.target.value)} /></Field>
                </div>
                <div className="px-5 py-4 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving?'Saving…':'Save'}</button>
                </div>
            </form>
        </div>
    );
};
const Field = ({ label, children, span2 }) => (
    <div className={span2 ? 'col-span-2' : ''}>
        <label className="block text-xs font-medium text-neutral-700 mb-1">{label}</label>{children}
    </div>
);

export default Properties;
